"use client";
import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, useVideoConfig, interpolate } from "remotion";
import { loadFont } from "@remotion/google-fonts/NotoSansDevanagari";
import { HOLES_DATA_URI } from "./trimbakHolesData";
import { DevaKathaLS2 } from "./DevaKathaLS2";
import { DevaKathaLS5 } from "./DevaKathaLS5";

const { fontFamily } = loadFont("normal", { weights: ["400", "700", "800", "900"], subsets: ["devanagari", "latin"] });

// Rath Yatra phrase overlays — Trimbakeshwar holes treatment. Top-left, yellow-prominent,
// per-word colour + line breaks, black drop-shadow, scattered-holes mask, word-by-word drop-in.

const PAD = 25;
const YELLOW = "#FFD60A";
const WHITE = "#FFFFFF";
const RED = "#FF3B2E";
const SH = "0 5px 14px rgba(0,0,0,0.9), 0 2px 5px rgba(0,0,0,0.95)";
const HOLES = HOLES_DATA_URI;

const BASE_FS = 66, BIG_FS = 92, SMALL_FS = 46;
const STAGGER = 0.16, DROP = 26, DUR = 0.32;
const LH = 1.18;
const LINE_GAP = -10;

type Word = { t: string; c?: "y" | "w" | "r"; big?: boolean; small?: boolean };
export type Phrase = { lines: Word[][]; start: number; out: number; lineGap?: number; lh?: number; ls?: string };
const col = (c?: string) => (c === "w" ? WHITE : c === "r" ? RED : YELLOW);

// 28 cards. start = exact word_timestamps_hindi anchor (verified). out = start + ~3.3.
export const RATH_PHRASES: Phrase[] = [
  { start: 0.00,    out: 4.80,    lines: [[{ t: "हज़ारों", c: "y", big: true }, { t: "हाथ", c: "y" }], [{ t: "एक", small: true }, { t: "रस्सी", c: "w" }]] },
  { start: 5.46,    out: 8.80,    lines: [[{ t: "45", c: "w", big: true }, { t: "फ़ीट", c: "y" }, { t: "ऊँचा", c: "y" }], [{ t: "16", c: "w" }, { t: "पहिये", small: true }]] },
  { start: 18.43,   out: 21.80,   lines: [[{ t: "रथ", c: "y" }], [{ t: "हिलता", c: "r", big: true }, { t: "नहीं", c: "r" }]] },
  { start: 46.52,   out: 49.90,   lines: [[{ t: "सलबेगा", c: "y", big: true }]] },
  { start: 50.45,   out: 53.80,   lines: [[{ t: "मंदिर", small: true }, { t: "में", small: true }], [{ t: "प्रवेश", c: "w" }, { t: "वर्जित", c: "r", big: true }]] },
  { start: 60.64,   out: 64.00,   lines: [[{ t: "रथ", c: "y" }, { t: "चला", c: "y" }], [{ t: "अपने", c: "w", big: true }, { t: "आप", c: "w" }]] },
  { start: 114.98,  out: 117.40,  lines: [[{ t: "1200+", c: "w", big: true }], [{ t: "लकड़ी", c: "y" }, { t: "के", small: true }, { t: "टुकड़े", small: true }]] },
  { start: 118.61,  out: 122.00,  lines: [[{ t: "एक", small: true }, { t: "भी", small: true }, { t: "कील", c: "r", big: true }, { t: "नहीं", c: "r" }], [{ t: "शून्य", c: "y" }, { t: "लोहा", small: true }]] },
  { start: 138.19,  out: 141.60,  lines: [[{ t: "राजा", small: true }, { t: "की", small: true }], [{ t: "सोने", c: "y" }, { t: "की", small: true }, { t: "झाड़ू", c: "y", big: true }]] },
  { start: 205.21,  out: 208.60,  lines: [[{ t: "108", c: "w", big: true }, { t: "कलश", c: "y" }], [{ t: "स्नान", c: "w" }, { t: "पूर्णिमा", small: true }]] },
  { start: 224.78,  out: 228.20,  lines: [[{ t: "भगवान", c: "y" }], [{ t: "बीमार", c: "r", big: true }, { t: "पड़ते", small: true }, { t: "हैं", small: true }]] },
  { start: 246.48,  out: 249.90,  lines: [[{ t: "15", c: "w", big: true }, { t: "दिन", c: "w" }], [{ t: "एकांतवास", c: "y" }]] },
  { start: 271.60,  out: 275.00,  lines: [[{ t: "नवयौवन", c: "y", big: true }], [{ t: "दर्शन", c: "w" }]] },
  { start: 293.53,  out: 296.90,  lines: [[{ t: "इकलौता", c: "y", big: true }, { t: "मंदिर", c: "y" }], [{ t: "जहाँ", small: true }, { t: "भगवान", c: "w" }, { t: "बीमार", c: "r" }]] },
  { start: 338.94,  out: 342.30,  lines: [[{ t: "नंदीघोष", c: "y", big: true }], [{ t: "जगन्नाथ", c: "w" }, { t: "का", small: true }, { t: "रथ", small: true }]] },
  { start: 365.80,  out: 369.20,  lines: [[{ t: "तालध्वज", c: "y", big: true }], [{ t: "बलभद्र", c: "w" }, { t: "का", small: true }, { t: "रथ", small: true }]] },
  { start: 385.47,  out: 388.80,  lines: [[{ t: "दर्पदलन", c: "y", big: true }], [{ t: "सुभद्रा", c: "w" }, { t: "का", small: true }, { t: "रथ", small: true }]] },
  { start: 398.75,  out: 402.10,  lines: [[{ t: "सारथी", c: "w" }], [{ t: "अर्जुन", c: "y", big: true }]] },
  { start: 527.43,  out: 530.80,  lines: [[{ t: "मॉर्टिस-टेनन", c: "y", big: true }], [{ t: "जोड़", c: "w" }, { t: "—", small: true }, { t: "कील", c: "r" }, { t: "ज़ीरो", small: true }]] },
  { start: 656.38,  out: 659.80,  lines: [[{ t: "छेरा", c: "y", big: true }, { t: "पहरा", c: "y" }], [{ t: "राजा", c: "w" }, { t: "झाड़ू", small: true }, { t: "लगाता", small: true }]] },
  { start: 964.48,  out: 967.80,  lines: [[{ t: "सुना", c: "y", big: true }, { t: "भेष", c: "y" }], [{ t: "स्वर्ण", c: "w" }, { t: "श्रृंगार", small: true }]] },
  { start: 968.70,  out: 972.10,  lines: [[{ t: "208", c: "w", big: true }, { t: "किलो", c: "w" }], [{ t: "ठोस", small: true }, { t: "सोना", c: "y" }]] },
  { start: 1002.38, out: 1005.80, lines: [[{ t: "माँ", c: "y" }, { t: "लक्ष्मी", c: "y" }], [{ t: "नाराज़", c: "r", big: true }]] },
  { start: 1038.43, out: 1041.80, lines: [[{ t: "रसगुल्ले", c: "w", big: true }, { t: "से", small: true }], [{ t: "मनाते", c: "y" }, { t: "हैं", small: true }]] },
  { start: 1064.94, out: 1068.30, lines: [[{ t: "खिचड़ी", c: "y", big: true }], [{ t: "प्रसाद", c: "w" }]] },
  { start: 1073.89, out: 1077.30, lines: [[{ t: "करमा", c: "y", big: true }, { t: "बाई", c: "y" }], [{ t: "की", small: true }, { t: "खिचड़ी", c: "w" }]] },
  { start: 1241.69, out: 1245.10, lines: [[{ t: "Juggernaut", c: "w", big: true }], [{ t: "जगन्नाथ", c: "y" }, { t: "से", small: true }]] },
];

const PhraseBlock: React.FC<{ p: Phrase; fps: number }> = ({ p, fps }) => {
  const frame = useCurrentFrame();
  const t = frame / fps;
  const phraseOp = interpolate(t, [p.out - 0.4, p.out], [1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  if (t < p.start - 0.1 || t > p.out + 0.1) return null;
  const gap = p.lineGap ?? LINE_GAP;
  const lh = p.lh ?? LH;
  return (
    <div style={{ position: "absolute", top: PAD, left: PAD, fontFamily, opacity: phraseOp, display: "flex", flexDirection: "column" }}>
      {p.lines.map((line, li) => (
        <div key={li} style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: 16, marginTop: li === 0 ? 0 : gap }}>
          {line.map((w, gi) => {
            const aT = p.start + (li * 2 + gi) * STAGGER;
            const k = Math.min(Math.max((t - aT) / DUR, 0), 1);
            const ease = 1 - Math.pow(1 - k, 3);
            const op = interpolate(t, [aT, aT + 0.18], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const y = -DROP * (1 - ease);
            return (
              <span key={gi} style={{
                color: col(w.c),
                fontSize: w.big ? BIG_FS : w.small ? SMALL_FS : BASE_FS,
                fontWeight: w.big ? 900 : 800,
                lineHeight: lh,
                letterSpacing: p.ls ?? "0",
                textShadow: SH,
                opacity: op,
                transform: `translateY(${y}px)`,
                display: "inline-block",
              }}>{w.t}</span>
            );
          })}
        </div>
      ))}
    </div>
  );
};

// Bottom-left viewing tip — half the size of the date, white, NO holes mask.
const WatchTag: React.FC<{ fps: number; start: number; out: number }> = ({ fps, start, out }) => {
  const t = useCurrentFrame() / fps;
  if (t < start - 0.1 || t > out + 0.1) return null;
  const op = interpolate(t, [start, start + 0.4, out - 0.4, out], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", bottom: PAD + 6, left: PAD, fontFamily, opacity: op }}>
      <span style={{ color: WHITE, fontSize: SMALL_FS, fontWeight: 700, letterSpacing: "0.01em", textShadow: SH }}>
        Watch at 1.2x for best experience
      </span>
    </div>
  );
};

// Master comp — holes-masked devotional phrases + non-masked UI (watch tip, LS pills).
export const RathYatraOverlaysFull: React.FC<{ phrases?: Phrase[] }> = ({ phrases = RATH_PHRASES }) => {
  const { fps } = useVideoConfig();
  const S = (s: number) => Math.round(s * fps);
  return (
    <AbsoluteFill>
      {/* holes distress mask over the dramatic top-left phrases only */}
      <AbsoluteFill style={{
        backgroundColor: "transparent",
        WebkitMaskImage: `url(${HOLES})`, maskImage: `url(${HOLES})`,
        WebkitMaskSize: "1280px 704px", maskSize: "1280px 704px",
        WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
        WebkitMaskPosition: "0 0", maskPosition: "0 0",
      }}>
        {phrases.map((p, i) => <PhraseBlock key={i} p={p} fps={fps} />)}
      </AbsoluteFill>

      {/* viewing tip (bottom-left, half-size), shows over the opening */}
      <WatchTag fps={fps} start={1.0} out={6.5} />

      {/* Like+Subscribe — LS2 @ early "सब्सक्राइब" ~179s · LS5 @ outro "सब्सक्राइब" ~1339s */}
      <Sequence from={S(179.42)} durationInFrames={S(5.2)}><DevaKathaLS2 /></Sequence>
      <Sequence from={S(1339.44)} durationInFrames={S(5.2)}><DevaKathaLS5 /></Sequence>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
